import { isInstalledPWA } from "@/utils/pwa";

/**
 * Hook to check if transitions should be disabled.
 * Returns true if ?noTransitions=1 is present (DEV testing) OR if running as installed PWA.
 */
export const useNoTransitionsFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Auto-disable in installed PWA mode to prevent whole-screen softness
  if (isInstalledPWA()) return true;
  return window.location.search.includes('noTransitions=1');
};

/**
 * Inject a global style that disables root-level transitions in PWA mode.
 * This prevents whole-screen GPU compositing that causes text softness.
 * Call once at app root level.
 */
export const injectNoTransitionsStyle = (): void => {
  if (typeof window === 'undefined') return;
  
  const isPWA = isInstalledPWA();
  const isDebugFlag = window.location.search.includes('noTransitions=1');
  
  // Only apply in PWA mode or when debug flag is present
  if (!isPWA && !isDebugFlag) return;

  const styleId = 'pwa-crisp-rendering';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* PWA CRISP RENDERING: Disable GPU promotion on ROOT CONTAINERS ONLY.
       This prevents whole-screen softness during tab switches.
       
       SCOPE: Only targets specific root wrappers, NOT:
       - Buttons, cards, icons (micro-animations preserved)
       - Modals, dialogs, tooltips (positioning preserved)
       - Sticky/fixed headers or nav (positioning preserved)
       - Scroll containers (performance preserved)
    */
    
    /* Root app containers only: no GPU layers */
    #root,
    #root > [data-testid="bootstrap-gate-wrapper"] {
      transition: none !important;
      transform: none !important;
      will-change: auto !important;
      backface-visibility: visible !important;
    }
    
    /* Dashboard route wrapper */
    [data-testid="dashboard-root"] {
      transition: none !important;
      transform: none !important;
      will-change: auto !important;
    }
    
    /* Tab content: allow opacity fade but no transforms */
    .tab-transition,
    [role="tabpanel"] > div:first-child {
      transition: opacity 0.1s ease-out !important;
      transform: none !important;
      will-change: auto !important;
    }
    
    /* Main content area: no root-level GPU promotion */
    main:not(.allow-transform) {
      transform: none !important;
      will-change: auto !important;
      /* Transitions allowed for opacity-based fades */
    }
    
    ${isDebugFlag ? `
    /* DEBUG MODE ONLY: All transitions disabled for A/B testing */
    *, *::before, *::after {
      transition-duration: 0ms !important;
      animation-duration: 0ms !important;
    }
    ` : ''}
  `;
  document.head.appendChild(style);

  // eslint-disable-next-line no-console
  if (import.meta.env.DEV) {
    console.log(`[PWA Crisp] Root transitions disabled (PWA: ${isPWA}, debug: ${isDebugFlag})`);
  }
};
